import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const PropiedadContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PropiedadProvider = ({ children }) => {
  const { usuario, isAuthenticated, isAdmin } = useAuth();
  const [propiedades, setPropiedades] = useState([]);
  const [propiedadSeleccionada, setPropiedadSeleccionada] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      cargarPropiedades();
    } else {
      setPropiedadSeleccionada(null);
    }
  }, [isAuthenticated, usuario]);

  const cargarPropiedades = async () => {
    try {
      const response = await axios.get(`${API}/propiedades`);
      let propiedadesFiltradas = response.data;
      
      // Si el usuario no es admin, filtrar solo las propiedades asignadas
      if (!isAdmin() && usuario?.propiedades_asignadas) {
        propiedadesFiltradas = response.data.filter(p => 
          usuario.propiedades_asignadas.includes(p.id_propiedad)
        );
      }
      
      setPropiedades(propiedadesFiltradas);
      
      // Buscar Chicureo como propiedad predeterminada
      const chicureo = propiedadesFiltradas.find(p => 
        (p.nombre || '').trim().toLowerCase().includes('chicureo')
      );
      const propiedadPorDefecto = chicureo || propiedadesFiltradas[0];

      // Si viene de un login reciente, forzar la propiedad por defecto (Chicureo)
      const forzarDefault = sessionStorage.getItem('forzar_propiedad_default') === 'true';
      if (forzarDefault) {
        sessionStorage.removeItem('forzar_propiedad_default');
        if (propiedadPorDefecto) {
          setPropiedadSeleccionada(propiedadPorDefecto);
          return;
        }
      }

      // Seleccionar Chicureo por defecto si no hay propiedad seleccionada
      if (propiedadesFiltradas.length > 0 && !propiedadSeleccionada) {
        setPropiedadSeleccionada(propiedadPorDefecto);
      }
      
      // Si hay una propiedad seleccionada, actualizarla con los datos nuevos
      if (propiedadSeleccionada) {
        const propiedadActualizada = propiedadesFiltradas.find(p => p.id_propiedad === propiedadSeleccionada.id_propiedad);
        if (propiedadActualizada) {
          setPropiedadSeleccionada(propiedadActualizada);
        } else if (propiedadesFiltradas.length > 0) {
          // Si la propiedad seleccionada ya no está disponible, seleccionar la predeterminada
          setPropiedadSeleccionada(propiedadPorDefecto);
        }
      }
    } catch (error) {
      console.error('Error al cargar propiedades:', error);
    } finally {
      setLoading(false);
    }
  };

  const cambiarPropiedad = (propiedadId) => {
    const propiedad = propiedades.find(p => p.id_propiedad === propiedadId);
    if (propiedad) {
      setPropiedadSeleccionada(propiedad);
    }
  };

  return (
    <PropiedadContext.Provider
      value={{
        propiedades,
        propiedadSeleccionada,
        cambiarPropiedad,
        cargarPropiedades,
        loading
      }}
    >
      {children}
    </PropiedadContext.Provider>
  );
};

export const usePropiedad = () => {
  const context = useContext(PropiedadContext);
  if (!context) {
    throw new Error('usePropiedad debe ser usado dentro de PropiedadProvider');
  }
  return context;
};
